import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Clock, Feather, Link2, Linkedin, PenLine } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { POSTS } from "@/data/posts";
import { LINKEDIN } from "@/data/content";

const wordCount = (post) => post.body.join(" ").split(/\s+/).length;

export const essayUrl = (id) => `${window.location.origin}${window.location.pathname}#/journal/${id}`;

export const EssayPage = () => {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.id === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="min-h-screen bg-paper flex flex-col items-center justify-center px-6 text-center" data-testid="essay-not-found">
        <p className="font-mono text-xs uppercase tracking-[0.24em] text-amber mb-4">404 — Lost in the drafts</p>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight">This essay doesn't exist.</h1>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-ink text-paper font-mono text-[11px] uppercase tracking-[0.18em] hover:bg-wine transition-colors duration-300" data-testid="essay-not-found-home-link">
          <ArrowLeft size={14} /> Back to the portfolio
        </Link>
      </div>
    );
  }

  const shareUrl = essayUrl(post.id);
  const related = POSTS.filter((p) => p.id !== post.id).slice(0, 3);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Essay link copied — share it anywhere.");
    } catch {
      toast.error("Could not copy the link. Copy it from the address bar instead.");
    }
  };

  const infographicsFor = (paragraph) =>
    (post.infographics || []).filter((inf) => !paragraph.startsWith("## ") && paragraph.startsWith(inf.anchor));

  return (
    <div className="bg-paper text-ink font-sans min-h-screen" data-testid="essay-page">
      <Seo
        title={`${post.title} — Melinda Adams`}
        siteName="Melinda Adams"
        description={post.excerpt}
        image={post.image || "/melinda-portrait.webp"}
      />

      <header className="sticky top-0 z-50 bg-paper/85 backdrop-blur-md border-b border-ink/10">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 h-[64px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="essay-home-link">
            <span className="w-8 h-8 rounded-full bg-wine text-paper flex items-center justify-center transition-transform duration-500 group-hover:rotate-[360deg]">
              <PenLine size={13} strokeWidth={1.75} />
            </span>
            <span className="font-serif text-base tracking-tight hidden sm:inline">
              Melinda <em className="not-italic text-wine">Adams</em>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/20 font-mono text-[10px] uppercase tracking-[0.16em] text-subtle hover:border-wine hover:text-wine transition-colors duration-300"
              data-testid="essay-back-link"
            >
              <ArrowLeft size={13} /> All essays
            </Link>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ink text-paper font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-wine transition-colors duration-300"
              data-testid="essay-copy-link-button"
            >
              <Link2 size={13} /> Copy link
            </button>
          </div>
        </div>
      </header>

      <motion.article
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-3xl mx-auto px-6 sm:px-10 pt-16 pb-24"
      >
        <div className="flex flex-wrap items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-subtle">
          <span className="text-wine">{post.tag}</span>
          <span>/</span>
          <span>{post.date}</span>
          <span>/</span>
          <span className="inline-flex items-center gap-1"><Clock size={11} /> {post.readTime}</span>
          <span>/</span>
          <span data-testid="essay-word-count">{wordCount(post)} words</span>
        </div>

        <h1 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.12]" data-testid="essay-title">
          {post.title}
        </h1>

        <p className="mt-8 font-serif italic text-xl text-wine leading-snug border-l-2 border-wine pl-5">
          {post.excerpt}
        </p>

        {post.image && (
          <div className="mt-10 relative overflow-hidden border border-line spotlight-frame">
            <img
              src={post.image}
              alt={`${post.title} — article illustration`}
              className="w-full object-cover"
              data-testid="essay-hero-image"
            />
          </div>
        )}

        <div className="mt-12 flex flex-col gap-6" data-testid="essay-body">
          {post.body.map((p, i) => (
            <div key={i}>
              {p.startsWith("## ") ? (
                <h2 className="font-serif text-xl sm:text-2xl tracking-tight mt-8 pt-6 border-t border-line">{p.slice(3)}</h2>
              ) : (
                <p className={`leading-relaxed ${i === 0 ? "text-lg text-ink" : "text-base text-ink/85"}`}>{p}</p>
              )}
              {infographicsFor(p).map((inf) => (
                <figure key={inf.src} className="my-10">
                  <img
                    src={inf.src}
                    alt={inf.alt}
                    className="w-full border border-line"
                    data-testid={`essay-infographic-${inf.src.split("/").pop().replace(".png", "")}`}
                  />
                  <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-subtle">
                    {inf.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          ))}
        </div>

        {post.linkedin && (
          <p className="mt-10 font-serif text-lg text-ink/85">
            Read more at{" "}
            <a
              href={LINKEDIN}
              target="_blank"
              rel="noopener noreferrer"
              className="text-wine underline decoration-wine/40 underline-offset-4 hover:decoration-wine transition-[text-decoration-color] duration-300"
              data-testid="essay-linkedin-link"
            >
              LinkedIn
            </a>
            .
          </p>
        )}

        <div className="mt-14 bg-cream border border-line p-7 flex flex-col sm:flex-row sm:items-center gap-5 justify-between" data-testid="essay-share-panel">
          <div>
            <p className="font-serif text-xl tracking-tight">Worth passing along?</p>
            <p className="mt-1 text-sm text-subtle">Every essay has its own link.</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-ink text-paper font-mono text-[10px] uppercase tracking-[0.16em] hover:bg-wine transition-colors duration-300"
              data-testid="essay-share-copy-button"
            >
              <Link2 size={13} /> Copy link
            </button>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-ink/20 font-mono text-[10px] uppercase tracking-[0.16em] hover:border-wine hover:text-wine transition-colors duration-300"
              data-testid="essay-share-linkedin-link"
            >
              <Linkedin size={13} /> Share
            </a>
          </div>
        </div>

        <div className="mt-12 border-t border-line pt-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-wine text-paper flex items-center justify-center">
              <Feather size={13} />
            </span>
            <div>
              <div className="font-serif text-base leading-tight">Melinda Adams</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-subtle">Copywriter &amp; Content Writer</div>
            </div>
          </div>
          <a
            href={LINKEDIN}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-ink/20 font-mono text-[10px] uppercase tracking-[0.16em] text-subtle hover:border-wine hover:text-wine transition-colors duration-300"
            data-testid="essay-author-linkedin-link"
          >
            <Linkedin size={13} /> Follow on LinkedIn
          </a>
        </div>

        <div className="mt-16">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-6">Keep reading</p>
          <div className="grid sm:grid-cols-3 gap-5" data-testid="essay-related">
            {related.map((r) => (
              <Link
                key={r.id}
                to={`/journal/${r.id}`}
                className="group bg-cream border border-line p-5 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(26,24,21,0.35)] transition-[transform,box-shadow] duration-500"
                data-testid={`essay-related-${r.id}`}
              >
                <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-wine">{r.tag}</span>
                <h3 className="mt-2 font-serif text-lg leading-snug tracking-tight">{r.title}</h3>
                <span className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-ink group-hover:text-wine transition-colors duration-300">
                  Read <ArrowUpRight size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </motion.article>

      <Footer />
    </div>
  );
};
